/* eslint-disable react-hooks/exhaustive-deps */
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from '../../assets/images/info.svg';
import CustomToolTip from '../../core/CustomToolTip/CustomToolTip';

import {
	getApiDailyParamName,
	getApiSubRegionRegionName,
	postApiSeaRatesGetD2D,
	postApiSeaRatesGetD2DAir,
	postApiTaricTaricAdValorenDTN,
} from '../../api/interconectar-api';
import { useAuth } from '../../context/AuthContext';
import { useVideo } from '../../context/VideoContext';
import { useRemainingCalculations } from '../../hooks/useRemainingCalculations';
import CircularLoading from '../../shared/CircularLoading/CircularLoading';
import IconError from '../Icons/IconError';
import IconPro from '../Icons/IconPro';
import { PaisesDestino } from './PaisesDestino';
import './PriceCalculator.css';
import { Country } from './PriceCalculator.types';
import truckTypeOptions from './truckTypeOptions.json';
import { IconDropDownArrow } from '../Icons';

const PriceCalculatorStepThree = ({
	handleClickOpen,
	handleContinueClick,
}: {
	handleClickOpen: MouseEventHandler<HTMLDivElement>;
	handleContinueClick: () => void;
}) => {
	const { t, i18n } = useTranslation();
	const { state } = useAuth();
	const {
		data: remainingCalcs,
		loading: loadingCalcs,
		error: errorCalcs,
	} = useRemainingCalculations(process.env.REACT_APP_PRICE_CALCULATOR_ID || '353');
	const { setVideoName } = useVideo();
	const [formThreeValues, setFormThreeValues] = useState<any>();
	const [newCountries, setCountries] = useState<any[]>([]);
	const [loadingCountries, setLoadingCountries] = useState<boolean>(true);
	const [errorCountries, setErrorCountries] = useState<string | null>(null);
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [loadingTaric, setLoadingTaric] = useState<boolean>(true);
	const [loadingGastoAduanero, setLoadingGastoAduanero] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

	const formTwo = JSON.parse(window.localStorage.getItem('formTwo') || '{}');

	const {
		register,
		handleSubmit,
		reset,
		getValues,
		formState: { errors },
	} = useForm();

	const onSubmit = (data: any) => {
		localStorage.setItem('formThree', JSON.stringify(data));
		setFormThreeValues(data);
		handleContinueClick();
	};

	useEffect(() => {
		const formThree = localStorage.getItem('formThree') ? JSON.parse(localStorage.getItem('formThree')!) : {};

		if (Object.keys(formThree).length) {
			// Si tenemos countryDestinationId pero no countryDestinationName, intentamos establecerlo
			if (formThree.countryDestinationId && !formThree.countryDestinationName) {
				// Buscar el nombre del país basado en el ID
				let countryName = '';
				newCountries.forEach((region) => {
					const foundCountry = region.countries.find(
						(country: Country) => country.id === formThree.countryDestinationId,
					);
					if (foundCountry) {
						countryName = foundCountry.name;
					}
				});

				if (countryName) {
					formThree.countryDestinationName = countryName;
				}
			}

			// Asegurarse de que importerTruckType tenga un valor válido
			if (formThree.importerTruckType) {
				// Verificar si el valor existe en truckTypeOptions (case-insensitive)
				const truckTypeKeys = Object.keys(truckTypeOptions[0]);
				const truckTypeExists = truckTypeKeys.some(
					(key) => key.toLowerCase() === formThree.importerTruckType.toLowerCase(),
				);

				if (!truckTypeExists) {
					// Establecer un valor por defecto si no es válido
					formThree.importerTruckType = truckTypeKeys[0];
				} else {
					// Si existe pero con diferente case, actualizar al valor correcto del objeto
					const correctKey = truckTypeKeys.find(
						(key) => key.toLowerCase() === formThree.importerTruckType.toLowerCase(),
					);
					if (correctKey && correctKey !== formThree.importerTruckType) {
						formThree.importerTruckType = correctKey;
					}
				}
			}

			setFormThreeValues(formThree);
			reset(formThree); // Reseteamos el formulario con los valores del localStorage
		}
	}, [newCountries, reset]); // Añadimos reset a las dependencias

	useEffect(() => {
		if (!state?.isAuthenticated || !remainingCalcs) {
			setLoadingTaric(false);
			return;
		}

		const formOne = JSON.parse(localStorage.getItem('formOne') || '{}');

		if (!formOne?.originCode) {
			setLoadingTaric(false);
			// console.log('Faltan datos necesarios en formOne');
			return;
		}

		if (state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining) {
			const fetchTaricData = async () => {
				console.log('Iniciando fetchTaricData');
				try {
					if (!formOne?.originCode || !formOne?.tarifCode) {
						console.log('Faltan datos necesarios en formOne');
						const storedValues = localStorage.getItem('formThree')
							? JSON.parse(localStorage.getItem('formThree')!)
							: {};
						const newValues = {
							...storedValues,
							adValoremTax: storedValues.adValoremTax || 0,
							taxDeterminedByWeight: storedValues.taxDeterminedByWeight || 0,
						};
						localStorage.setItem('formThree', JSON.stringify(newValues));
						setFormThreeValues(newValues);
						reset(newValues);
						setLoadingTaric(false);
						return;
					}

					// console.log('Llamando a la API con:', {
					//     countryOriginCode: formOne.originCode,
					//     goodsCode: formOne.tarifCode
					// });

					const response = await postApiTaricTaricAdValorenDTN({
						countryOriginCode: formOne.originCode,
						goodsCode: formOne.tarifCode,
					});

					// console.log('Respuesta de la API:', response);

					const storedValues = localStorage.getItem('formThree') ? JSON.parse(localStorage.getItem('formThree')!) : {};

					let newValues;
					if (response.result && response.result.adValoren !== undefined && response.result.dtn !== undefined) {
						newValues = {
							...storedValues,
							adValoremTax: response.result.adValoren,
							taxDeterminedByWeight: response.result.dtn,
						};
					} else {
						console.log('No se encontraron valores en la respuesta');
						newValues = {
							...storedValues,
							adValoremTax: 0,
							taxDeterminedByWeight: 0,
						};
					}

					//console.log('Actualizando valores:', newValues);
					localStorage.setItem('formThree', JSON.stringify(newValues));
					setFormThreeValues(newValues);
					reset(newValues);
				} catch (error) {
					console.error('Error en fetchTaricData:', error);
					const storedValues = localStorage.getItem('formThree') ? JSON.parse(localStorage.getItem('formThree')!) : {};

					const newValues = {
						...storedValues,
						adValoremTax: 0,
						taxDeterminedByWeight: 0,
					};

					localStorage.setItem('formThree', JSON.stringify(newValues));
					setFormThreeValues(newValues);
					reset(newValues);
				} finally {
					console.log('Finalizando fetchTaricData');
					setLoadingTaric(false);
				}
			};

			console.log('Ejecutando fetchTaricData');
			fetchTaricData();
		}
	}, [remainingCalcs, state?.isAuthenticated, reset]);

	// Añadimos un useEffect para mantener sincronizado el formulario con formThreeValues
	useEffect(() => {
		if (formThreeValues) {
			const currentFormValues = getValues();
			const newValues = {
				...currentFormValues,
				...formThreeValues,
			};
			reset(newValues);
		}
	}, [formThreeValues, reset, getValues]);

	// Modificamos los inputs para que sean controlados directamente por formThreeValues
	const handleInputChange = (field: string, value: string) => {
		const newValues = {
			...formThreeValues,
			[field]: value,
		};
		setFormThreeValues(newValues);
		reset(newValues);
		localStorage.setItem('formThree', JSON.stringify(newValues));

		// Marcar que importerTruckType cambió para trigger la API call
		if (field === 'importerTruckType') {
			localStorage.setItem('importerTruckTypeChanged', 'true');
		}
	};

	useEffect(() => {
		if (formTwo?.conveyance) {
			formTwo.conveyance === 'maritimo' || formTwo.conveyance === 'multimodal'
				? setVideoName('thirdBarcoVideo')
				: formTwo.conveyance === 'aereo'
					? setVideoName('thirdAvionVideo')
					: setVideoName('');
		} else {
			setVideoName('');
		}
	}, [formTwo?.conveyance]);

	// Combine all loading states
	useEffect(() => {
		const anyLoading = loadingCountries || loadingTaric || loadingGastoAduanero || loadingCalcs;
		setIsLoading(anyLoading);

		// Si no hay ningún loading y es la carga inicial, marcamos que ya no es carga inicial
		if (!anyLoading && isInitialLoad) {
			setIsInitialLoad(false);
		}
	}, [loadingCountries, loadingTaric, loadingGastoAduanero, loadingCalcs, isInitialLoad]);

	useEffect(() => {
		const fetchCountries = async () => {
			try {
				const data = await getApiSubRegionRegionName('Europa');
				const storedData = JSON.parse(localStorage.getItem('formOne') || '{}');
				const excludedId = storedData?.countryOriginId;

				if (data.results) {
					const filteredCountries = data.results.map((region: any) => ({
						...region,
						countries: region.countries.filter((country: any) => country.id !== excludedId),
					}));

					setCountries(filteredCountries);
				}
			} catch (error) {
				setErrorCountries(t('price_calculator.step_3.error_message_fetch'));
			} finally {
				setLoadingCountries(false);
			}
		};

		fetchCountries();
	}, []);

	useEffect(() => {
		if (
			state?.isAuthenticated &&
			remainingCalcs &&
			remainingCalcs.remaining > 0 &&
			!formThreeValues?.customersExpensesValueUsd
		) {
			setLoadingGastoAduanero(true);
			getApiDailyParamName('GastoAduanero')
				.then((response) => {
					if (response.result?.value) {
						setFormThreeValues((prev: Record<string, any>) => ({
							...prev,
							customersExpensesValueUsd: response.result?.value,
						}));
					}
				})
				.catch((error) => {
					console.error('Error fetching GastoAduanero:', error);
				})
				.finally(() => {
					setLoadingGastoAduanero(false);
				});
		}
	}, [state?.isAuthenticated, remainingCalcs, formThreeValues?.customersExpensesValueUsd]);

	const [loadingSeaRates, setLoadingSeaRates] = useState(false);
	const [errorSeaRates, setErrorSeaRates] = useState<string | null>(null);
	const [seaRatesData, setSeaRatesData] = useState<any>(null);

	// Api SeaRates GetD2D - Door to Door service
	useEffect(() => {
		if (state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining > 0) {
			if (formThreeValues?.importerCity && formThreeValues?.importerTruckType) {
				// Verificar si formTwo cambió (portDestinationName) o si importerCity cambió
				const formTwoChanged = localStorage.getItem('formTwoChanged') === 'true';
				const importerCityChanged = localStorage.getItem('importerCityChanged') === 'true';
				const importerTruckTypeChanged = localStorage.getItem('importerTruckTypeChanged') === 'true';

				// Hacer la llamada a la API si formTwo cambió, importerCity cambió, o importerTruckType cambió
				if (formTwoChanged || importerCityChanged || importerTruckTypeChanged) {
					console.log(
						'Haciendo llamada a la API D2D - formTwo cambió:',
						formTwoChanged,
						'importerCity cambió:',
						importerCityChanged,
						'importerTruckType cambió:',
						importerTruckTypeChanged,
					);

					const fetchD2D = async () => {
						setLoadingSeaRates(true);
						setErrorSeaRates(null);
						try {
							const formOneStr = localStorage.getItem('formOne');
							const formOne = formOneStr ? JSON.parse(formOneStr) : {};
							const formTwoStr = localStorage.getItem('formTwo');
							const formTwo = formTwoStr ? JSON.parse(formTwoStr) : {};

							// Obtener el nombre del puerto de destino del StepTwo
							const portDestinationName = formTwo?.portDestinationName || null;

							// Verificar si es transporte aéreo
							const isAirFreight = formTwo?.meansTransportation === 'Air';

							let response;

							if (isAirFreight) {
								// Para transporte aéreo
								const airRequestData = {
									airportOriginId: formTwo?.airportId,
									destinationCity: formThreeValues?.importerCity || null,
									volumeInCubicMeters: parseFloat(formTwo?.airTarifCode) || 0,
									weightKg: parseFloat(formTwo?.airTotalWeightPlusPackaging) || 0,
								};
								console.log('D2D Air Request data:', airRequestData);
								response = await postApiSeaRatesGetD2DAir(airRequestData);
							} else {
								// Para transporte marítimo/multimodal
								const seaRequestData = {
									containerTypeId: formTwo?.containerId,
									destinationCity: formThreeValues?.importerCity || null,
									originCity: portDestinationName || null,
								};
								console.log('D2D Sea Request data:', seaRequestData);
								response = await postApiSeaRatesGetD2D(seaRequestData);
							}
							console.log('D2D Response:', response);

							if (response?.result?.routeTariffPrice !== undefined) {
								setSeaRatesData(response.result);

								// Actualizar el campo costTransportationPortWarehouse con routeTariffPrice
								const updatedFormValues = {
									...formThreeValues,
									costTransportationPortWarehouse: response.result.routeTariffPrice.toString(),
								};

								setFormThreeValues(updatedFormValues);
								// setValue('costTransportationPortWarehouse', response.result.routeTariffPrice.toString());
							}
						} catch (err: any) {
							console.error('Error fetching D2D:', err);
							setErrorSeaRates(err.message || 'Error desconocido');
						} finally {
							setLoadingSeaRates(false);
						}
					};

					fetchD2D();
				} else {
					console.log('No se hace llamada a la API D2D - no hay cambios relevantes');
				}
			}
		}
	}, [state?.isAuthenticated, remainingCalcs, formThreeValues?.importerCity, formThreeValues?.importerTruckType]);

	useEffect(() => {
		if (formTwo.dthcPrice !== undefined && formTwo.impPrice !== undefined) {
			const suma = Number(formTwo.dthcPrice) + Number(formTwo.impPrice);

			// Solo aplicar si aún no hay valor en portExpensesUsd
			if (!formThreeValues?.portExpensesUsd) {
				// Obtener todos los valores existentes del formThree del localStorage
				const existingFormThree = localStorage.getItem('formThree')
					? JSON.parse(localStorage.getItem('formThree')!)
					: {};

				const newValues = {
					...existingFormThree, // Preservar TODOS los valores del localStorage
					...formThreeValues, // Mantener los valores actuales del estado
					portExpensesUsd: suma,
				};
				setFormThreeValues(newValues);
				reset(newValues);
				localStorage.setItem('formThree', JSON.stringify(newValues));
			}
		}
	}, []);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	// Mostrar loading solo durante la carga inicial
	if (isInitialLoad && isLoading) {
		return (
			<CircularLoading />
			// <div className="loading-container">
			//     <p className="loading-text">{t('price_calculator.loading_message')}</p>
			// </div>
		);
	}

	if (errorCountries) {
		return (
			<div className="errorcalculator">
				<div className="errorcalculator__icon">
					<IconError />
				</div>
				<p className="errorcalculator__txt">{errorCountries}</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="exporter-test__read-only">
				<div className="price-calculator__container-main">
					<div className="price-calculator___inner">
						<div className="container price-calculator___container">
							<div className="row">
								<div className="col-12">
									<span className="price-calculator__container-title">{t('price_calculator.step_3.subtitle')}</span>
								</div>
							</div>

							<div className="row price-calculator__row mb-4">
								<div className="col-12 col-sm-12 col-md-6">
									<div className="price-calculator__field-with-icon label-container container_input__pro">
										<input
											type="number"
											className="price-calculator__textbox-general-col2"
											{...register('portExpensesUsd', { required: true })}
											value={!!formThreeValues?.portExpensesUsd ? formThreeValues?.portExpensesUsd : ''}
											onChange={(e) => handleInputChange('portExpensesUsd', e.target.value)}
										/>
										<label className="label-input">{t('price_calculator.step_3.portExpensesUsd.placeholder')}</label>

										<div style={{ marginLeft: '8px' }}>
											<CustomToolTip
												title=""
												tooltipText={t('price_calculator.step_3.portExpensesUsd.tooltip')}>
												<FontAwesomeIcon
													icon={faCircleQuestion}
													className="font-size: 18px"
												/>
											</CustomToolTip>
										</div>
									</div>
									<div className="label__pro">
										<IconPro
											width={13}
											height={10}
											fill="#BBC2C7"
										/>
										{t('price_calculator.label_pro.description')}
									</div>
									{!!errors?.portExpensesUsd && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_3.error_message')}</div>
										</div>
									)}
								</div>
								<div className="col-12 col-sm-12 col-md-6">
									<div className="price-calculator__field-with-icon label-container container_input__pro">
										<input
											type="number"
											className="price-calculator__textbox-general-col2"
											value={
												!!formThreeValues?.customersExpensesValueUsd ? formThreeValues?.customersExpensesValueUsd : ''
											}
											{...register('customersExpensesValueUsd', { required: true })}
											onChange={(e) => handleInputChange('customersExpensesValueUsd', e.target.value)}
										/>
										<label className="label-input">
											{t('price_calculator.step_3.customersExpensesValueUsd.placeholder')}
										</label>
										<div style={{ marginLeft: '8px' }}>
											<CustomToolTip
												title=""
												tooltipText={t('price_calculator.step_3.customersExpensesValueUsd.tooltip')}>
												<FontAwesomeIcon
													icon={faCircleQuestion}
													className="font-size: 18px"
												/>
											</CustomToolTip>
										</div>
									</div>
									<div className="label__pro">
										<IconPro
											width={13}
											height={10}
											fill="#BBC2C7"
										/>
										{t('price_calculator.label_pro.description')}
									</div>
									{!!errors?.customersExpensesValueUsd && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_3.error_message')}</div>
										</div>
									)}
								</div>
							</div>

							<div className="row price-calculator__row mb-4">
								<div className="col-12 col-sm-12 col-md-6">
									<div className="price-calculator__field-with-icon">
										<div className="label-container">
											<label className="label-input">
												{t('price_calculator.step_3.countryDestinationId.placeholder')}
											</label>
											<select
												{...register('countryDestinationId', { required: true })}
												value={formThreeValues?.countryDestinationId || ''} // Valor controlado del select
												onChange={(e) => {
													const selectedCountryId = e.target.value;

													// Buscar el país dentro de `newCountries`
													let selectedCountryName = '';
													newCountries.forEach((region) => {
														const foundCountry = region.countries.find(
															(country: Country) => country.id === selectedCountryId,
														);
														if (foundCountry) {
															selectedCountryName = foundCountry.name;
														}
													});

													setFormThreeValues({
														...formThreeValues,
														countryDestinationId: selectedCountryId,
														countryDestinationName: selectedCountryName,
														country: selectedCountryId,
														countryOriginId: selectedCountryId,
														paisSelect: '',
													});
												}}
												className="label-container__input input-label-select">
												<option
													value=""
													disabled>
													{t('select.placeholder')}
												</option>

												{newCountries.map((region) => (
													<optgroup
														key={region.nameEs}
														label={region.nameEs}>
														{region.countries.map((country: Country) => (
															<option
																key={country.id}
																value={country.id}
																className="price-calculator__select-font">
																{country.name}
															</option>
														))}
													</optgroup>
												))}
											</select>
											<IconDropDownArrow className="input-label-select-arrow" />
										</div>
										<div style={{ marginLeft: '8px' }}>
											<CustomToolTip
												title=""
												tooltipText={t('price_calculator.step_3.countryDestinationId.tooltip')}>
												<FontAwesomeIcon
													icon={faCircleQuestion}
													className="font-size: 18px"
												/>
											</CustomToolTip>
										</div>
									</div>
									{!!errors?.countryDestinationId && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_3.error_message')}</div>
										</div>
									)}
								</div>
								<div className="col-12 col-md-3 label-container container_input__pro">
									<input
										type="number"
										className="price-calculator__textbox-general-col2"
										{...register('adValoremTax', { required: true })}
										value={!!formThreeValues?.adValoremTax ? formThreeValues?.adValoremTax : ''}
										onChange={(e) => handleInputChange('adValoremTax', e.target.value)}
									/>
									<label className="label-input">{t('price_calculator.step_3.adValoremTax.placeholder')}</label>

									<div className="label__pro">
										<IconPro
											width={13}
											height={10}
											fill="#BBC2C7"
										/>
										{t('price_calculator.label_pro.description')}
									</div>
									{!!errors?.adValoremTax && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_3.error_message')}</div>
										</div>
									)}
								</div>
								<div className="col-12 col-md-3 label-container container_input__pro">
									<input
										type="number"
										className="price-calculator__textbox-general-col2"
										{...register('taxDeterminedByWeight', { required: true })}
										value={!!formThreeValues?.taxDeterminedByWeight ? formThreeValues?.taxDeterminedByWeight : ''}
										onChange={(e) => handleInputChange('taxDeterminedByWeight', e.target.value)}
									/>
									<label className="label-input">
										{t('price_calculator.step_3.taxDeterminedByWeight.placeholder')}
									</label>

									<div className="label__pro">
										<IconPro
											width={13}
											height={10}
											fill="#BBC2C7"
										/>
										{t('price_calculator.label_pro.description')}
									</div>
									{!!errors?.taxDeterminedByWeight && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_3.error_message')}</div>
										</div>
									)}
								</div>
							</div>

							<div className="row price-calculator__row ">
								<div className="col-12 col-sm-12 col-md-6 mb-4">
									<div className="label-container">
										<label className="label-input">{t('price_calculator.step_3.importerCity.placeholder')}</label>
										<select
											{...register('importerCity', { required: true })}
											value={formThreeValues?.importerCity || ''} // Valor controlado del select
											onChange={(e) => {
												const selectedPais = e.target.value;

												setFormThreeValues({
													...formThreeValues,
													importerCity: selectedPais,
												});

												// Marcar que importerCity cambió para trigger la API call
												localStorage.setItem('importerCityChanged', 'true');
											}}
											className={`label-container__input input-label-select ${errors?.importerCity ? 'error' : ''}`}>
											<option
												value=""
												disabled>
												{t('select.placeholder')}
											</option>

											{formThreeValues?.countryDestinationName &&
												Array.isArray(PaisesDestino[formThreeValues.countryDestinationName]) &&
												PaisesDestino[formThreeValues.countryDestinationName].map((city) => (
													<option
														key={city}
														value={city}>
														{city}
													</option>
												))}
										</select>
										<IconDropDownArrow className="input-label-select-arrow" />
									</div>

									{!!errors?.importerCity && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_3.error_message')}</div>
										</div>
									)}
								</div>
								<div className="col-12 col-sm-12 col-md-6 ">
									{state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining > 0 && (
										<div className="col-12 col-sm-12 col-md-12 mb-4">
											<div className="price-calculator__distance">
												<div className="price-calculator__field-with-icon label-container ">
													<div className="label-container">
														<label className="label-input">
															{t('price_calculator.step_3.camionAduana.placeholder')}
														</label>
														<select
															{...register('importerTruckType', { required: true })}
															value={formThreeValues?.importerTruckType || ''}
															onChange={(e) => {
																const selectedCamion = e.target.value;
																setFormThreeValues({
																	...formThreeValues,
																	importerTruckType: selectedCamion,
																});
															}}
															className={`label-container__input input-label-select ${errors?.importerTruckType ? 'error' : ''}`}>
															<option
																value=""
																disabled>
																{t('select.placeholder')}
															</option>

															{Object.entries(truckTypeOptions[0]).map(
																([key, value]: [string, { es: string; en: string; hi: string }]) => {
																	const currentLang = i18n.language;
																	let lang: 'es' | 'en' | 'hi' = 'es';
																	if (currentLang === 'en') lang = 'en';
																	if (currentLang === 'hi') lang = 'hi';
																	// Si el idioma comienza con 'es', lo tratamos como español
																	if (currentLang.startsWith('es')) lang = 'es';
																	return (
																		<option
																			key={key}
																			value={key}>
																			{value[lang] || key}
																		</option>
																	);
																},
															)}
														</select>
														<IconDropDownArrow className="input-label-select-arrow" />
													</div>
													<div style={{ marginLeft: '8px' }}>
														<CustomToolTip
															title=""
															tooltipText={t('price_calculator.step_3.camionAduana.tooltip')}>
															<FontAwesomeIcon
																icon={faCircleQuestion}
																className="font-size: 18px"
															/>
														</CustomToolTip>
													</div>
												</div>

												{!!errors?.importerTruckType && (
													<div className="error-container">
														<InfoIcon fill="#F8A0D2" />
														<div className="error-message">{t('price_calculator.step_3.error_message')}</div>
													</div>
												)}
											</div>
										</div>
									)}

									<div className="price-calculator__distance container_input__pro">
										{loadingSeaRates && (
											<div className="price-calculator__field-loading">
												<CircularLoading />
											</div>
										)}

										{!loadingSeaRates && (
											<>
												<div className="price-calculator__field-with-icon label-container container_input__pro">
													<input
														type="number"
														className="price-calculator__textbox-general-col2"
														{...register('costTransportationPortWarehouse', { required: true })}
														value={
															!!formThreeValues?.costTransportationPortWarehouse
																? formThreeValues?.costTransportationPortWarehouse
																: ''
														}
														onChange={(e) => handleInputChange('costTransportationPortWarehouse', e.target.value)}
													/>
													<label className="label-input">
														{t('price_calculator.step_3.costTransportationPortWarehouse.placeholder')}
													</label>

													<div style={{ marginLeft: '8px' }}>
														<CustomToolTip
															title=""
															tooltipText={t('price_calculator.step_3.costTransportationPortWarehouse.tooltip')}>
															<FontAwesomeIcon
																icon={faCircleQuestion}
																className="font-size: 18px"
															/>
														</CustomToolTip>
													</div>
												</div>
												<div className="label__pro">
													<IconPro
														width={13}
														height={10}
														fill="#BBC2C7"
													/>
													{t('price_calculator.label_pro.description')}
												</div>
												{(!!errors?.distance || !!errors?.costTransportationPortWarehouse) && (
													<div className="error-container">
														<InfoIcon fill="#F8A0D2" />
														<div className="error-message">{t('price_calculator.step_3.error_message')}</div>
													</div>
												)}
											</>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="price-calculator__container-footer">
						<div
							className="price-calculator__footer-link"
							onClick={handleClickOpen}>
							{t('price_calculator.footer.link')}
						</div>
						<button
							type="submit"
							className="price-calc__next">
							<div className="btn-test-next-text">{t('price_calculator.footer.button')}</div>
							<FontAwesomeIcon
								icon={faArrowRight}
								color="black"
							/>
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};

export default PriceCalculatorStepThree;
